import React from 'react';
import {
    MenuItem,
    Select,
    Checkbox,
    SelectChangeEvent,
    Typography,
    AutocompleteRenderInputParams,
    TextField,
    Autocomplete
} from '@mui/material';
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { Column } from 'ui/Flex';
import { Option } from 'lib/types';

const checkedIcon = <CheckBoxIcon sx={{ fontSize: '14px' }} />;
const uncheckedIcon = <CheckBoxOutlineBlankIcon sx={{ fontSize: '14px' }} />;

interface MuiGridSelectProps extends GridRenderEditCellParams {
    options: Option[];
    multiple?: boolean;
    verifyValue?: boolean;
}

export const MuiGridSelect: React.FC<MuiGridSelectProps> = ({
    value,
    id,
    api,
    field,
    options,
    multiple,
    verifyValue,
    error
}) => {
    const [open, setOpen] = React.useState(true);
    const handleChange = React.useCallback(
        (event: SelectChangeEvent) => {
            let newValue: string | string[];
            if (Array.isArray(event.target.value) && verifyValue) {
                newValue = event.target.value.filter(item => options.some(option => option.value === item));
            } else {
                newValue = event.target.value;
            }
            api.setEditCellValue({ id, field, value: newValue }, event);
            if (!multiple) {
                api.stopCellEditMode({ id, field });
            }
        },
        [api, field, id, multiple, options, verifyValue]
    );
    const handleOnBlur = React.useCallback(() => {
        api.stopCellEditMode({ id, field });
    }, [api, field, id]);
    const handleClose = React.useCallback(() => {
        setOpen(false);
        const isEditing = api.getCellMode(id, field) === 'edit';
        if (isEditing) {
            api.stopCellEditMode({ id, field });
        }
    }, [api, field, id]);
    const renderOption = React.useCallback(
        ({ description: localDescription, value, label: optionLabel }: Option) => (
            <MenuItem key={`select-${optionLabel}-${value}`} value={value}>
                {localDescription ? (
                    <Column>
                        {optionLabel}
                        <Typography variant="caption">{localDescription}</Typography>
                    </Column>
                ) : (
                    optionLabel
                )}
            </MenuItem>
        ),
        []
    );
    const renderValue = React.useCallback(
        (value: string[]) =>
            Array.isArray(value)
                ? value.map(item => options.find(option => item === option.value)?.label).join(', ')
                : undefined,
        [options]
    );
    // : FIXME: GRID_VALIDATION

    return (
        <Select
            value={value || (multiple ? [] : '')} // "" || undefined
            fullWidth
            onChange={handleChange}
            open={open}
            onClose={handleClose}
            multiple={multiple}
            renderValue={renderValue}
            onBlur={handleOnBlur}
            error={!!error}
        >
            {options && options.map(renderOption)}
        </Select>
    );
};

export const MuiGridAutoComplete: React.FC<MuiGridSelectProps> = ({
    value,
    id,
    api,
    field,
    options,
    multiple
}) => {
    const [open, setOpen] = React.useState(true);
    const [inputValue, setInputValue] = React.useState('');

    const [selectedOptions, setSelectedOptions] = React.useState<Option[] | Option | null>(() => {
        if (multiple) {
            if (Array.isArray(value) && value.length > 0) {
                return options.filter(option => value.includes(option.value)).map(option => ({ ...option }));
            }
            return [];
        }
        if (value && typeof value === 'string') {
            const found = options.find(option => option.value === value);
            return found ? { ...found } : null;
        }
        return null;
    });

    const sortedOptions = React.useMemo(() => {
        if (multiple) {
            const selectedArr = Array.isArray(selectedOptions) ? selectedOptions : [];
            if (!selectedArr.length) {
                return options.map(option => ({ ...option }));
            }
            const selectedValues = selectedArr.map(opt => opt.value);
            const selectedSet = new Set(selectedValues);
            const selected = selectedValues
                .map(val => options.find(opt => opt.value === val))
                .filter(Boolean)
                .map(option => ({ ...option }));
            const unselected = options
                .filter(opt => !selectedSet.has(opt.value))
                .map(option => ({ ...option }));
            return [...selected, ...unselected];
        }
        return options.map(option => ({ ...option }));
    }, [options, selectedOptions, multiple]);

    const handleChange = React.useCallback(
        (_: never, selected: Option | Option[] | null) => {
            if (multiple) {
                const selectedArr = Array.isArray(selected) ? selected : [];
                const cleanSelectedOptions = selectedArr.map(option => ({
                    value: option.value,
                    label: option.label
                }));
                const newValue = cleanSelectedOptions
                    .filter(item => options.some(option => option.value === item.value))
                    .map(item => item.value);
                setSelectedOptions(cleanSelectedOptions);
                api.setEditCellValue({ id, field, value: newValue });
            } else {
                const cleanSelectedOption =
                    selected && !Array.isArray(selected)
                        ? { value: selected.value, label: selected.label }
                        : null;
                setSelectedOptions(cleanSelectedOption);
                api.setEditCellValue({
                    id,
                    field,
                    value: cleanSelectedOption ? cleanSelectedOption.value : null
                });
                api.stopCellEditMode({ id, field });
            }
        },
        [api, field, id, multiple, options]
    );

    const handleOnBlur = React.useCallback(() => {
        api.stopCellEditMode({ id, field });
    }, [api, field, id]);

    const handleClose = React.useCallback(() => {
        setOpen(false);
        const isEditing = api.getCellMode(id, field) === 'edit';
        if (isEditing) {
            api.stopCellEditMode({ id, field });
        }
    }, [api, field, id]);

    const handleInputChange = React.useCallback(
        (_: never, newInputValue: string, reason: string) => {
            if (reason === 'reset' && !!multiple) {
                return;
            }
            setInputValue(newInputValue);
        },
        [multiple]
    );

    const renderOptionItem = React.useCallback(
        (props, option, { selected }) => {
            if (!multiple) {
                return (
                    <li {...props} key={option.value}>
                        <Typography variant="body2">{option.label}</Typography>
                    </li>
                );
            }
            return (
                <li {...props} key={option.value} style={{ paddingInline: 1 }}>
                    <Checkbox
                        icon={uncheckedIcon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 2 }}
                        checked={selected}
                        size="small"
                    />
                    <Typography variant="body2">{option.label}</Typography>
                </li>
            );
        },
        [multiple]
    );

    const renderInput = React.useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField
                {...params}
                autoFocus
                sx={{
                    paddingInline: 1
                }}
                variant="standard"
                size="small"
                InputProps={{ sx: { fontSize: '0.85rem' }, disableUnderline: true, ...params.InputProps }}
                placeholder="Search"
            />
        ),
        []
    );

    const renderTags = React.useCallback(() => null, []);

    const equalityCheck = React.useCallback((option, value) => option.value === value.value, []);

    const getOptionLabel = React.useCallback(option => option.label || '', []);

    return (
        <Autocomplete
            value={selectedOptions}
            options={sortedOptions}
            fullWidth
            size="small"
            onChange={handleChange}
            open={open}
            onClose={handleClose}
            onBlur={handleOnBlur}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            renderOption={renderOptionItem}
            renderInput={renderInput}
            disableCloseOnSelect={!!multiple}
            multiple={!!multiple}
            disableClearable
            renderTags={multiple ? renderTags : undefined}
            isOptionEqualToValue={equalityCheck}
            getOptionLabel={getOptionLabel}
        />
    );
};

export function renderMuiGridSelect(props: GridRenderEditCellParams, options: Option[]) {
    return <MuiGridSelect {...props} options={options} />;
}

export function renderMuiGridMultiSelect(
    props: GridRenderEditCellParams,
    options: Option[],
    verify?: boolean
) {
    return <MuiGridSelect {...props} options={options} multiple verifyValue={verify} />;
}

export function renderMuiGridMultiAutoComplete(
    props: GridRenderEditCellParams,
    options: Option[],
    verify?: boolean
) {
    return <MuiGridAutoComplete {...props} options={options} multiple verifyValue={verify} />;
}

export function renderMuiGridSingleAutoComplete(
    props: GridRenderEditCellParams,
    options: Option[],
    verify?: boolean
) {
    return <MuiGridAutoComplete {...props} options={options} verifyValue={verify} />;
}
